// Shared e-invoice / e-way bill helpers for the sales invoice screens.

// E-way bill transport details (NIC codes). Needed when the invoice value is above the e-way bill threshold (Rs. 50,000).
export const emptyTransport = {
  transportMode: "1",
  distanceKm: "",
  vehicleNumber: "",
  vehicleType: "R",
  transporterId: "",
  transporterName: "",
  transportDocNo: "",
  transportDocDate: "",
};

export const transportModes = [
  { value: "1", label: "Road" },
  { value: "2", label: "Rail" },
  { value: "3", label: "Air" },
  { value: "4", label: "Ship" },
];

export const toTransportForm = (transport) => {
  if (!transport) return { ...emptyTransport };
  return {
    transportMode: transport.transportMode || (transport.vehicleNumber ? "1" : emptyTransport.transportMode),
    distanceKm: transport.distanceKm ?? "",
    vehicleNumber: transport.vehicleNumber || "",
    vehicleType: transport.vehicleType || "R",
    transporterId: transport.transporterId || "",
    transporterName: transport.transporterName || "",
    transportDocNo: transport.transportDocNo || "",
    transportDocDate: transport.transportDocDate || "",
  };
};

// Null when nothing beyond the default mode was entered, so invoices below the e-way bill threshold carry no transport details.
export const toTransportPayload = (transport) => {
  const road = transport.transportMode === "1";
  const text = (value) => (value && String(value).trim() ? String(value).trim() : null);
  const entered = [transport.distanceKm, transport.vehicleNumber, transport.transporterId, transport.transporterName,
    transport.transportDocNo, transport.transportDocDate].some((value) => text(value) !== null);
  if (!entered && road) return null;
  return {
    transportMode: transport.transportMode || null,
    distanceKm: transport.distanceKm === "" ? null : Number(transport.distanceKm),
    vehicleNumber: road ? text(transport.vehicleNumber) : null,
    vehicleType: road && text(transport.vehicleNumber) ? transport.vehicleType : null,
    transporterId: text(transport.transporterId),
    transporterName: text(transport.transporterName),
    transportDocNo: text(transport.transportDocNo),
    transportDocDate: transport.transportDocDate || null,
  };
};

// ---- document status

const isOutstanding = (doc) => doc?.status === "PENDING" || doc?.status === "FAILED";

export const hasEInvoicePdf = (invoice) => Boolean(invoice?.gst?.eInvoice?.pdfUrl);
export const hasEWayBillPdf = (invoice) => Boolean(invoice?.gst?.eWayBill?.pdfUrl);

// Generation is offered only while something is still to be generated; once the documents are generated (or not needed) it is disabled.
export const canGenerateGst = (invoice) =>
  invoice?.status !== "CANCELLED" && (isOutstanding(invoice?.gst?.eInvoice) || isOutstanding(invoice?.gst?.eWayBill));

export const gstStatusBadgeClass = {
  NOT_REQUIRED: "badge-gray",
  PENDING: "badge-amber",
  GENERATED: "badge-green",
  FAILED: "badge-red",
  CANCELLED: "badge-red",
};

export const gstStatusLabel = {
  NOT_REQUIRED: "N/A",
  PENDING: "Pending",
  GENERATED: "Generated",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

// ---- transport form

export const TransportFields = ({ transport, onChange }) => {
  const upper = (e) => onChange({ target: { name: e.target.name, value: e.target.value.toUpperCase() } });
  const road = transport.transportMode === "1";
  return (
    <div className="form-grid">
      <div className="form-group">
        <label>Transport Mode</label>
        <select name="transportMode" value={transport.transportMode} onChange={onChange}>
          {transportModes.map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Approx. Distance (km)</label>
        <input
          type="number"
          min="0"
          max="4000"
          step="1"
          name="distanceKm"
          value={transport.distanceKm}
          onChange={onChange}
          placeholder="0 = auto from PIN codes"
        />
      </div>
      {road ? (
        <>
          <div className="form-group">
            <label>Vehicle Number</label>
            <input name="vehicleNumber" maxLength={20} value={transport.vehicleNumber} onChange={upper} placeholder="e.g. KA01AB1234" />
          </div>
          <div className="form-group">
            <label>Vehicle Type</label>
            <select name="vehicleType" value={transport.vehicleType} onChange={onChange}>
              <option value="R">Regular</option>
              <option value="O">Over-dimensional cargo</option>
            </select>
          </div>
        </>
      ) : null}
      <div className="form-group">
        <label>Transport Doc No.{!road ? " *" : " (optional)"}</label>
        <input
          name="transportDocNo"
          maxLength={15}
          value={transport.transportDocNo}
          onChange={onChange}
          placeholder="LR / RR / AWB / BL number"
          required={!road}
        />
      </div>
      <div className="form-group">
        <label>Transport Doc Date{!road ? " *" : " (optional)"}</label>
        <input type="date" name="transportDocDate" value={transport.transportDocDate} onChange={onChange} required={!road} />
      </div>
      <div className="form-group">
        <label>Transporter ID (GSTIN / TRANSIN)</label>
        <input name="transporterId" maxLength={15} value={transport.transporterId} onChange={upper} placeholder="Optional" />
      </div>
      <div className="form-group">
        <label>Transporter Name</label>
        <input name="transporterName" maxLength={100} value={transport.transporterName} onChange={onChange} placeholder="Optional" />
      </div>
    </div>
  );
};
